import { migrate } from './migrations.js';
import type { ProgressRepository } from './progress-repository.js';
import {
  defaultProfile,
  HISTORY_RETENTION_PER_SKILL,
  PROFILE_CORRUPT_KEY,
  PROFILE_STORAGE_KEY,
  type Attempt,
  type Profile,
} from './types.js';

/**
 * `localStorage`-backed `ProgressRepository`.
 *
 * - Single namespaced, schema-versioned key.
 * - Save after every recorded attempt (no debounce; profile is small).
 * - Corrupted-blob fallback: parse failure → fresh default + bad value
 *   backed up to a sibling key for diagnostics.
 * - History is bounded per-skill (oldest dropped on overflow).
 */
export class LocalStorageProgressRepository implements ProgressRepository {
  private storage: Storage;

  constructor(storage: Storage = localStorage) {
    this.storage = storage;
  }

  async load(): Promise<Profile> {
    const raw = this.storage.getItem(PROFILE_STORAGE_KEY);
    if (raw === null) return defaultProfile();

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.backupAndReset(raw);
      return defaultProfile();
    }

    const migrated = migrate(parsed);
    // If migration produced a default and the input was clearly not the default
    // (i.e. some value existed but didn't validate), back it up.
    if (migrated === defaultProfile() || !isCleanProfile(parsed)) {
      // Only back up when the input was actually present but unparseable as a profile.
      if (raw !== JSON.stringify(defaultProfile()) && !isCleanProfile(parsed)) {
        this.backupAndReset(raw);
      }
    }
    return migrated;
  }

  async save(profile: Profile): Promise<void> {
    this.storage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
  }

  async recordAttempt(attempt: Attempt): Promise<void> {
    const profile = await this.load();
    const skillHistory = profile.history[attempt.skill];
    skillHistory.push(attempt);
    if (skillHistory.length > HISTORY_RETENTION_PER_SKILL) {
      skillHistory.splice(0, skillHistory.length - HISTORY_RETENTION_PER_SKILL);
    }
    await this.save(profile);
  }

  private backupAndReset(raw: string): void {
    try {
      this.storage.setItem(PROFILE_CORRUPT_KEY, raw);
    } catch {
      // ignore: backup is best-effort.
    }
    this.storage.removeItem(PROFILE_STORAGE_KEY);
  }
}

function isCleanProfile(v: unknown): boolean {
  if (typeof v !== 'object' || v === null) return false;
  return (v as { schemaVersion?: unknown }).schemaVersion === 1;
}
